import * as React from "react";
import { ChangeEvent, MouseEvent, useMemo, useState } from "react";
import { DateTime } from "luxon";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableSortLabel from "@mui/material/TableSortLabel";
import Box from "@mui/material/Box";
import { visuallyHidden } from "@mui/utils";
import Toolbar from "@mui/material/Toolbar";
import { Button, TextField } from "@mui/material";
import Paper from "@mui/material/Paper";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TablePagination from "@mui/material/TablePagination";
import { getComparator, Order, stableSort } from "../../utils/Table";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import JsonViewer from "../../components/JSONViewer";

export interface ILiveDataOperation {
  id: string;
  sendTimeMessageId: string;
  playerId: string;
  content: object;
  contentDataType: string;
  createdAt: Date;
  createdBy: string;
  operationId: string;
  queueUrl: string;
  status: string;
  type: string;
}

type LiveOperationsTableHeadProperties = keyof ILiveDataOperation;

interface HeadCell {
  id: LiveOperationsTableHeadProperties;
  label: string;
  align?: "inherit" | "left" | "center" | "right" | "justify";
  padding?: "normal" | "checkbox" | "none";
}

const headCells: readonly HeadCell[] = [
  {
    id: "id",
    label: "ID",
    padding: "none",
  },
  {
    id: "type",
    label: "Type",
  },
  {
    id: "status",
    label: "Status",
  },
  {
    id: "operationId",
    label: "Operation ID",
  },
  {
    id: "contentDataType",
    label: "Content Data Type",
  },
  {
    id: "content",
    label: "Content",
  },
  {
    id: "playerId",
    label: "Player ID",
  },
  {
    id: "createdAt",
    label: "Created At",
    align: "right",
  },
  {
    id: "createdBy",
    label: "Created By",
    align: "right",
  },
];

interface LiveOperationsTableHeadProps {
  onRequestSort: (event: MouseEvent<unknown>, property: LiveOperationsTableHeadProperties) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
}

const LiveOperationsTableHead = ({ order, orderBy, onRequestSort }: LiveOperationsTableHeadProps) => {
  const createSortHandler = (property: LiveOperationsTableHeadProperties) => (event: MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="normal" />
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.align}
            padding={headCell.padding}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

interface LiveOperationsTableToolbarProps {
  onRefreshButtonClick: () => void;
  filterQuery: string;
  onFilterQueryChange: (value: string) => void;
  environment: string;
}

const LiveOperationsTableToolbar = ({
  onRefreshButtonClick,
  filterQuery,
  onFilterQueryChange,
}: LiveOperationsTableToolbarProps) => {
  return (
    <Toolbar sx={{ pl: { sm: 2 }, pr: { xs: 1, sm: 1 } }}>
      <Button variant="outlined" startIcon={<RefreshIcon />} onClick={onRefreshButtonClick} style={{ marginRight: 8 }}>
        Refresh
      </Button>
      <TextField
        id="outlined-search"
        label="Search by ID, Type, Status, Operation ID, Player ID"
        type="search"
        size="small"
        onChange={(e) => onFilterQueryChange(e.target.value)}
        sx={{ minWidth: "35%" }}
        value={filterQuery}
      />
    </Toolbar>
  );
};

export interface LiveOperationsTableProps {
  rows: ILiveDataOperation[];
  environment: string;
  onRefreshButtonClick: () => void;
}

export const LiveOperationsTable = ({ rows, environment, onRefreshButtonClick }: LiveOperationsTableProps) => {
  const [order, setOrder] = useState<Order>("desc");
  const [orderBy, setOrderBy] = useState<LiveOperationsTableHeadProperties>("createdAt");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filterQuery, setFilterQuery] = useState("");
  const [jsonViewerVisible, setJsonViewerVisible] = useState<boolean>(false);
  const [jsonData, setJsonData] = useState<string>("{}");

  const showContent = (content: object) => {
    setJsonData(JSON.stringify(content, null, 2));
    setJsonViewerVisible(true);
  };

  const requestSortHandler = (event: MouseEvent<unknown>, property: LiveOperationsTableHeadProperties) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const pageChangeHandler = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const rowsPerPageChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const filteredRows = useMemo(() => {
    return rows.filter(
      (t) =>
        t.id.toLowerCase().includes(filterQuery.toLowerCase()) ||
        t.type.toLowerCase().includes(filterQuery.toLowerCase()) ||
        t.status.toLowerCase().includes(filterQuery.toLowerCase()) ||
        t.operationId.toLowerCase().includes(filterQuery.toLowerCase()) ||
        t.playerId?.toLowerCase().includes(filterQuery.toLowerCase()),
    );
  }, [rows, filterQuery]);

  const visibleRows = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return stableSort(filteredRows as any, getComparator(order, orderBy)).slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );
  }, [filteredRows, order, orderBy, page, rowsPerPage]);

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2 }}>
        <LiveOperationsTableToolbar
          onRefreshButtonClick={onRefreshButtonClick}
          filterQuery={filterQuery}
          onFilterQueryChange={setFilterQuery}
          environment={environment}
        />
        <TableContainer>
          <Table sx={{ minWidth: 750 }} size="small">
            <LiveOperationsTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={requestSortHandler}
              rowCount={rows.length}
            />
            <TableBody>
              {visibleRows.map((row, index) => {
                const labelId = `live-operations-table-checkbox-${index}`;
                const createdAt = row.createdAt
                  ? DateTime.fromJSDate(new Date(row.createdAt)).toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS)
                  : "unknown";
                return (
                  <TableRow hover role="checkbox" tabIndex={-1} key={index} sx={{ cursor: "pointer" }}>
                    <TableCell padding="normal" />
                    <TableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      padding="none"
                      style={{ fontFamily: "monospace", fontSize: "0.75rem" }}
                    >
                      {row.id}
                    </TableCell>
                    <TableCell align="left">{row.type}</TableCell>
                    <TableCell align="left">{row.status}</TableCell>
                    <TableCell align="left" style={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
                      {row.operationId}
                    </TableCell>
                    <TableCell align="left">{row.contentDataType}</TableCell>
                    <TableCell align="left">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<VisibilityIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          showContent(row.content);
                        }}
                      >
                        Show
                      </Button>
                    </TableCell>
                    <TableCell align="left" style={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
                      {row.playerId}
                    </TableCell>
                    <TableCell align="right">{createdAt}</TableCell>
                    <TableCell align="right">{row.createdBy}</TableCell>
                  </TableRow>
                );
              })}
              {emptyRows > 0 && (
                <TableRow style={{ height: 33 * emptyRows }}>
                  <TableCell colSpan={8} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={pageChangeHandler}
          onRowsPerPageChange={rowsPerPageChangeHandler}
        />
      </Paper>
      <JsonViewer visible={jsonViewerVisible} data={jsonData} onClose={() => setJsonViewerVisible(false)} />
    </Box>
  );
};
